import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import RatingStars from "../components/RatingStars";
import { useAuth } from "../context/AuthContext";
import { supabase } from "../lib/supabase";
import { submitRating } from "../utils/rateUser";
import {
  fetchEarningsTimelineForBookings,
  getLatestTimelineEntry,
} from "../utils/marketplaceLifecycle";
import {
  getFinanceStateLabel,
  getFinanceStateTone,
  getClosureSummary,
  getCommissionExposure,
  getOtpStateLabel,
  getServiceLifecycleStage,
} from "../utils/serviceLifecycle";

function MyBookings() {
  const navigate = useNavigate();
  const { user, isLoggedIn } = useAuth();

  const [orders, setOrders] = useState([]);
  const [serviceBookings, setServiceBookings] = useState([]);
  const [materialOrders, setMaterialOrders] = useState([]);
  const [legacyBookings, setLegacyBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [ratingByBooking, setRatingByBooking] = useState({});
  const [reviewByBooking, setReviewByBooking] = useState({});
  const [ratingLoadingByBooking, setRatingLoadingByBooking] = useState({});
  const [ratedBookingIds, setRatedBookingIds] = useState({});

  useEffect(() => {
    const fetchAll = async () => {
      if (!isLoggedIn || !user?.id) {
        setOrders([]);
        setServiceBookings([]);
        setMaterialOrders([]);
        setLegacyBookings([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setErrorMessage("");

      try {
        const ordersQuery = () =>
          supabase
            .from("orders")
            .select(`
              *,
              addresses (
                house_flat_no,
                street_area,
                city,
                state,
                country,
                pincode
              )
            `);

        const [ordersResult, legacyBookingsResult] = await Promise.all([
          (async () => {
            const primaryOrdersResult = await ordersQuery()
              .or(`customer_profile_id.eq.${user.id},user_id.eq.${user.id}`)
              .order("created_at", { ascending: false });

            if (!primaryOrdersResult.error) {
              return primaryOrdersResult;
            }

            const missingColumnError =
              primaryOrdersResult.error.code === "PGRST204" ||
              /column/i.test(primaryOrdersResult.error.message || "");

            if (!missingColumnError) {
              return primaryOrdersResult;
            }

            return ordersQuery()
              .eq("user_id", user.id)
              .order("created_at", { ascending: false });
          })(),
          supabase
            .from("bookings")
            .select("*")
            .eq("customer_profile_id", user.id)
            .order("created_at", { ascending: false }),
        ]);

        if (ordersResult.error) throw ordersResult.error;
        if (legacyBookingsResult.error) throw legacyBookingsResult.error;

        const scopedOrders = ordersResult.data || [];
        const scopedLegacyBookings = legacyBookingsResult.data || [];
        const orderIds = scopedOrders.map((order) => order.id).filter(Boolean);

        if (orderIds.length === 0) {
          setOrders([]);
          setServiceBookings([]);
          setMaterialOrders([]);
          setLegacyBookings(scopedLegacyBookings);
          return;
        }

        const [serviceResult, materialResult] = await Promise.all([
          supabase
            .from("service_bookings")
            .select("*")
            .in("order_id", orderIds)
            .order("created_at", { ascending: false }),
          supabase
            .from("material_orders")
            .select("*")
            .in("order_id", orderIds)
            .order("created_at", { ascending: false }),
        ]);

        if (serviceResult.error) throw serviceResult.error;
        if (materialResult.error) throw materialResult.error;

        const orderAddressMap = scopedOrders.reduce((acc, order) => {
          acc[order.id] = getOrderAddress(order.addresses);
          return acc;
        }, {});

        const serviceRows = serviceResult.data || [];
        const serviceTimeline = await fetchEarningsTimelineForBookings(
          serviceRows.map((item) => item.id).filter(Boolean)
        );
        const serviceTimelineMap = serviceTimeline.reduce((acc, item) => {
          if (!acc[item.service_booking_id]) {
            acc[item.service_booking_id] = [];
          }
          acc[item.service_booking_id].push(item);
          return acc;
        }, {});
        const assignedWorkerIds = [
          ...new Set(serviceRows.map((item) => item.assigned_worker_id).filter(Boolean)),
        ];

        let workerLabelMap = {};

        if (assignedWorkerIds.length > 0) {
          const { data: workerRows, error: workerRowsError } = await supabase
            .from("workers")
            .select("id, profile_id, full_name, phone")
            .or(
              assignedWorkerIds
                .map((id) => `id.eq.${id},profile_id.eq.${id}`)
                .join(",")
            );

          if (workerRowsError) throw workerRowsError;

          workerLabelMap = (workerRows || []).reduce((acc, worker) => {
            const label =
              worker.full_name || worker.phone || worker.profile_id || worker.id;
            acc[worker.id] = label;
            if (worker.profile_id) {
              acc[worker.profile_id] = label;
            }
            return acc;
          }, {});
        }

        const materialRows = materialResult.data || [];
        const shopIds = [
          ...new Set(materialRows.map((item) => item.shop_id).filter(Boolean)),
        ];
        const assignedShopOwnerIds = [
          ...new Set(materialRows.map((item) => item.assigned_shop_id).filter(Boolean)),
        ];

        let shopRows = [];
        let shopLabelMap = {};
        let shopOwnerLabelMap = {};

        if (shopIds.length > 0) {
          const { data: fetchedShopRows, error: shopRowsError } = await supabase
            .from("shops")
            .select("id, shop_name, shop_owner_id")
            .in("id", shopIds);

          if (shopRowsError) throw shopRowsError;

          shopRows = fetchedShopRows || [];

          shopLabelMap = shopRows.reduce((acc, shop) => {
            acc[shop.id] = shop.shop_name || shop.shop_owner_id || shop.id;
            return acc;
          }, {});
        }

        const linkedShopOwnerIds = [
          ...new Set([
            ...shopRows.map((shop) => shop.shop_owner_id).filter(Boolean),
          ...assignedShopOwnerIds,
          ]),
        ];

        if (linkedShopOwnerIds.length > 0) {
          const { data: shopOwnerRows, error: shopOwnerRowsError } = await supabase
            .from("shop_owners")
            .select("id, business_name, owner_name, phone")
            .in("id", linkedShopOwnerIds);

          if (shopOwnerRowsError) throw shopOwnerRowsError;

          shopOwnerLabelMap = (shopOwnerRows || []).reduce((acc, owner) => {
            acc[owner.id] =
              owner.business_name || owner.owner_name || owner.phone || owner.id;
            return acc;
          }, {});
        }

        setOrders(scopedOrders);
        setServiceBookings(
          serviceRows.map((item) => ({
            ...item,
            orderCode:
              scopedOrders.find((order) => order.id === item.order_id)?.order_code ||
              item.order_id ||
              "N/A",
            latestFinanceState:
              getLatestTimelineEntry(serviceTimelineMap[item.id] || [])?.state || null,
            assignedWorkerLabel:
              item.status === "awaiting_manual_assignment"
                ? "Manual assignment review in progress"
                : item.status === "worker_reassigned"
                  ? `Reassigned to ${
                      workerLabelMap[item.assigned_worker_id] ||
                      item.assigned_worker_id ||
                      "new worker"
                    }`
                  : item.assigned_worker_id
                    ? workerLabelMap[item.assigned_worker_id] ||
                      item.assigned_worker_id
                    : "Worker not assigned yet",
          }))
        );
        setMaterialOrders(
          materialRows.map((item) => ({
            ...item,
            resolvedAddressText:
              item.delivery_address_text || orderAddressMap[item.order_id] || "N/A",
            assignedShopLabel:
              shopOwnerLabelMap[item.assigned_shop_id] ||
              shopLabelMap[item.shop_id] ||
              shopOwnerLabelMap[item.shop_id] ||
              item.assigned_shop_id ||
              item.shop_id ||
              "Shop assignment pending",
          }))
        );
        setLegacyBookings(scopedLegacyBookings);
      } catch (error) {
        console.error("MY BOOKINGS ERROR:", error);
        setErrorMessage(error.message || "Failed to load your orders.");
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [isLoggedIn, user]);

  const formatStatus = (value) => {
    if (!value) return "N/A";
    return value
      .replaceAll("_", " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const getOrderAddress = (address) => {
    if (!address) return "N/A";

    return [
      address.house_flat_no,
      address.street_area,
      address.city,
      address.state,
      address.country,
      address.pincode,
    ]
      .filter(Boolean)
      .join(", ");
  };

  const getServicesForOrder = (orderId) => {
    return serviceBookings.filter((item) => item.order_id === orderId);
  };

  const getMaterialsForOrder = (orderId) => {
    return materialOrders.filter((item) => item.order_id === orderId);
  };

  const getLegacyBookingHint = (booking) => {
    const status = booking?.status || "";

    if (status === "completed") {
      return "This direct booking is complete.";
    }

    if (["accepted", "in_progress"].includes(status)) {
      return "A worker is assigned and this direct booking is active.";
    }

    if (["cancelled", "declined"].includes(status)) {
      return "This direct booking needs attention or a new request.";
    }

    return "This direct booking is created and waiting for worker action.";
  };

  const getServiceStageLabel = (service) => {
    return getServiceLifecycleStage(service?.status || "");
  };

  const getServiceStageStyle = (service) => {
    const status = service?.status || "";

    if (status === "completed") {
      return stageBadgeDone;
    }

    if (
      [
        "accepted",
        "in_progress",
        "worker_assigned",
        "worker_reassigned",
        "assigned",
        "en_route",
        "arrived",
        "otp_pending_start",
        "completion_requested",
        "otp_pending_completion",
      ].includes(status)
    ) {
      return stageBadgeActive;
    }

    if (["cancelled", "declined", "awaiting_manual_assignment"].includes(status)) {
      return stageBadgeAlert;
    }

    return stageBadgePending;
  };

  const getTransportDisplayLabel = (service) => {
    if (service?.transport_responsibility === "customer_arranged") {
      return "Customer will bear transport";
    }

    if (service?.transport_responsibility === "mistribazaar_arranged") {
      return "MistriBazaar will arrange transport";
    }

    const notes = service?.notes || "";

    if (notes.includes("Transport responsibility: customer_arranged")) {
      return "Customer will bear transport";
    }

    if (notes.includes("Transport responsibility: mistribazaar_arranged")) {
      return "MistriBazaar will arrange transport";
    }

    return "Transport not specified";
  };

  const getMaterialStageLabel = (material) => {
    const status = material?.delivery_status || "";

    if (["delivered", "completed"].includes(status)) {
      return "Delivered";
    }

    if (
      ["shop_confirmed", "preparing_dispatch", "delivery_partner_assigned", "out_for_delivery"].includes(
        status
      )
    ) {
      return "In progress";
    }

    if (["cancelled", "failed", "rejected"].includes(status)) {
      return "Needs attention";
    }

    return "Awaiting shop confirmation";
  };

  const getMaterialStageStyle = (material) => {
    const status = material?.delivery_status || "";

    if (["delivered", "completed"].includes(status)) {
      return stageBadgeDone;
    }

    if (
      ["shop_confirmed", "preparing_dispatch", "delivery_partner_assigned", "out_for_delivery"].includes(
        status
      )
    ) {
      return stageBadgeActive;
    }

    if (["cancelled", "failed", "rejected"].includes(status)) {
      return stageBadgeAlert;
    }

    return stageBadgePending;
  };

  const getMaterialStageHint = (material) => {
    const status = material?.delivery_status || "";
    const hasAssignedShop =
      material?.assignedShopLabel &&
      material.assignedShopLabel !== "Shop assignment pending";

    if (["delivered", "completed"].includes(status)) {
      return "This material order has been completed and delivered.";
    }

    if (status === "shop_confirmed") {
      return "The shop has confirmed this order and should prepare dispatch next.";
    }

    if (status === "preparing_dispatch") {
      return "The shop is preparing this order for dispatch.";
    }

    if (status === "delivery_partner_assigned") {
      return "A delivery partner has been assigned for this order.";
    }

    if (status === "out_for_delivery") {
      return "This material order is out for delivery.";
    }

    if (["cancelled", "failed", "rejected"].includes(status)) {
      return "This material order needs attention or a support follow-up.";
    }

    if (!hasAssignedShop) {
      return "Your material order has been created and is waiting for shop assignment and confirmation.";
    }

    return "Your material order has been created and is waiting for shop confirmation.";
  };

  const handleRate = async (booking) => {
    const selectedRating = Number(ratingByBooking[booking.id] || 0);
    const review = (reviewByBooking[booking.id] || "").trim();
    const ratedUserId = booking.assigned_worker_id || booking.worker_id || null;

    if (!ratedUserId) {
      alert("No assigned worker found for this booking yet.");
      return;
    }

    if (!selectedRating) {
      alert("Please select a star rating before submitting.");
      return;
    }

    setRatingLoadingByBooking((prev) => ({
      ...prev,
      [booking.id]: true,
    }));

    try {
      await submitRating({
        bookingId: booking.id,
        ratedUserId,
        ratedRole: "worker",
        rating: selectedRating,
        review,
      });

      setRatedBookingIds((prev) => ({
        ...prev,
        [booking.id]: true,
      }));

      alert("Rating submitted successfully.");
    } catch (error) {
      console.error("RATING SUBMIT ERROR:", error);
      alert(error.message || "Failed to submit rating.");
    } finally {
      setRatingLoadingByBooking((prev) => ({
        ...prev,
        [booking.id]: false,
      }));
    }
  };

  return (
    <div style={page}>
      <div style={container} className="bookings-page__container">
        <button onClick={() => navigate("/")} style={backBtn} className="bookings-page__back-button">
          Back to Home
        </button>

        <div style={heroCard} className="bookings-page__hero">
          <h1 style={{ margin: 0 }} className="bookings-page__title">My Bookings & Orders</h1>
          <p style={heroText} className="bookings-page__hero-text">
            Track your service requests, material orders, delivery progress,
            and payment status.
          </p>
        </div>

        {!isLoggedIn && (
          <div style={emptyBox}>
            <p>Please log in to view your bookings.</p>
          </div>
        )}

        {loading && (
          <div style={emptyBox}>
            <p>Loading your orders...</p>
          </div>
        )}

        {!loading && errorMessage && (
          <div style={errorBox}>
            <p>{errorMessage}</p>
          </div>
        )}

        {!loading && isLoggedIn && orders.length === 0 && legacyBookings.length === 0 && (
          <div style={emptyBox}>
            <p>No bookings or orders found yet.</p>
          </div>
        )}

        {!loading && legacyBookings.length > 0 && (
          <div style={{ display: "grid", gap: "18px", marginTop: "24px" }}>
            <div style={card}>
              <h2 style={{ marginTop: 0 }}>Direct Worker Bookings</h2>
              <p style={heroText}>
                These are older direct bookings created from an individual worker profile.
              </p>

              <div style={{ display: "grid", gap: "10px" }}>
                {legacyBookings.map((booking) => (
                  <div key={booking.id} style={miniCard}>
                    <div style={serviceTopRow} className="bookings-page__service-top-row">
                      <strong>{booking.service_type || "Direct Worker Booking"}</strong>
                      <div style={getServiceStageStyle(booking)}>
                        {getServiceStageLabel(booking)}
                      </div>
                    </div>
                    <p style={muted}>
                      Booking Ref: {booking.display_user_id || booking.id}
                    </p>
                    <p style={muted}>
                      Date: {booking.booking_date || "N/A"} • Slot:{" "}
                      {booking.booking_slot || "To be assigned"}
                    </p>
                    <p style={muted}>Status: {formatStatus(booking.status)}</p>
                    <p style={muted}>
                      Address:{" "}
                      {booking.address_text ||
                        [
                          booking.flat_house_no,
                          booking.street_area,
                          booking.city,
                          booking.state,
                          booking.country,
                          booking.pincode,
                        ]
                          .filter(Boolean)
                          .join(", ") ||
                        "N/A"}
                    </p>
                    <p style={serviceHint}>{getLegacyBookingHint(booking)}</p>
                    <p style={muted}>Price: Rs {booking.price || 0}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {!loading && orders.length > 0 && (
          <div style={{ display: "grid", gap: "18px", marginTop: "24px" }}>
            {orders.map((order) => {
              const relatedServices = getServicesForOrder(order.id);
              const relatedMaterials = getMaterialsForOrder(order.id);

              return (
                <div key={order.id} style={card}>
                  <div style={topRow} className="bookings-page__top-row">
                    <div>
                      <h3 style={{ margin: 0 }}>{order.order_code}</h3>
                      <p style={muted}>
                        Created: {new Date(order.created_at).toLocaleString()}
                      </p>
                    </div>

                    <div style={statusWrap} className="bookings-page__status-wrap">
                      <div style={statusBadge}>{formatStatus(order.status)}</div>
                      <div style={statusBadgeMuted}>
                        Payment: {formatStatus(order.payment_status)}
                      </div>
                    </div>
                  </div>

                  <div style={infoGrid} className="bookings-page__info-grid">
                    <div style={infoBlock}>
                      <strong>Order Type</strong>
                      <p style={muted}>{formatStatus(order.order_type)}</p>
                    </div>

                    <div style={infoBlock}>
                      <strong>Membership</strong>
                      <p style={muted}>
                        {order.membership_name || "No Membership"}
                      </p>
                    </div>

                    <div style={infoBlock}>
                      <strong>Total Amount</strong>
                      <p style={price}>Rs {order.total_amount}</p>
                    </div>

                    <div style={infoBlock}>
                      <strong>Address</strong>
                      <p style={muted}>{getOrderAddress(order.addresses)}</p>
                    </div>
                  </div>

                  {relatedServices.length > 0 && (
                    <div style={section}>
                      <h4 style={sectionHeading}>Service Bookings</h4>
                      <div style={{ display: "grid", gap: "10px" }}>
                        {relatedServices.map((service) => (
                          <div key={service.id} style={miniCard}>
                            {(() => {
                              const closureSummary = getClosureSummary(service);
                              const commissionExposure = getCommissionExposure(service);

                              return (
                                <>
                            <div style={serviceTopRow} className="bookings-page__service-top-row">
                              <strong>{service.service_title}</strong>
                              <div style={getServiceStageStyle(service)}>
                                {getServiceStageLabel(service)}
                              </div>
                            </div>
                            <p style={muted}>
                              Category: {service.service_category || "general"}
                            </p>
                            <p style={muted}>
                            <p style={muted}>
                              Order ID: {service.orderCode}
                            </p>
                              Assigned Worker: {service.assignedWorkerLabel}
                            </p>
                            <p style={muted}>
                              Date: {service.booking_date} • Slot:{" "}
                              {service.booking_slot || "To be assigned"}
                            </p>
                            <p style={muted}>
                              Status: {formatStatus(service.status)}
                            </p>
                            <p style={muted}>
                              Transport: {getTransportDisplayLabel(service)}
                            </p>
                            <p style={muted}>
                              Address: {service.address_text || getOrderAddress(order.addresses)}
                            </p>
                            <p style={muted}>OTP Flow: {getOtpStateLabel(service)}</p>
                            <p
                              style={{
                                ...muted,
                                color:
                                  getFinanceStateTone(service) === "good"
                                    ? "#b8ffd0"
                                    : getFinanceStateTone(service) === "risk"
                                      ? "#ffb4b4"
                                      : "#c7c7c7",
                              }}
                            >
                              Earnings State: {getFinanceStateLabel(service)}
                            </p>
                            <p style={serviceHint}>
                              {service.status === "completed"
                                ? "This booking is complete. You can rate the worker below."
                                : service.status === "awaiting_manual_assignment"
                                  ? "This request needs manual assignment support. Our operations team should review it next."
                                  : service.status === "worker_reassigned"
                                    ? "Your booking has been reassigned to a new worker and is moving forward again."
                                : ["accepted", "in_progress", "worker_assigned", "worker_reassigned"].includes(
                                    service.status
                                  )
                                  ? "A worker has accepted this request and execution is underway or scheduled."
                                  : ["cancelled", "declined"].includes(service.status)
                                    ? "This request needs manual review or a fresh booking decision."
                                    : "Your request is created and waiting for worker acceptance."}
                            </p>
                            <p style={muted}>Rate: Rs {service.rate}</p>
                            <p style={muted}>Commission tracking: {commissionExposure.label}</p>
                            {closureSummary.hasClosureData ? (
                              <div style={ratingCard}>
                                <p style={ratingTitle}>Closure Summary</p>
                                <p style={muted}>
                                  Final value: Rs {closureSummary.finalValue.toLocaleString("en-IN")}
                                </p>
                                <p style={muted}>
                                  Advance: Rs {closureSummary.advance.toLocaleString("en-IN")} | Collected: Rs {closureSummary.collected.toLocaleString("en-IN")}
                                </p>
                                <p style={muted}>
                                  Completion: {closureSummary.completion}%
                                </p>
                                {service.closure_summary ? (
                                  <p style={muted}>{service.closure_summary}</p>
                                ) : null}
                              </div>
                            ) : null}

                            {service.status === "completed" && (
                              <div style={ratingCard}>
                                <p style={ratingTitle}>
                                  Rate your completed service
                                </p>
                                <RatingStars
                                  value={Number(ratingByBooking[service.id] || 0)}
                                  onChange={(value) =>
                                    setRatingByBooking((prev) => ({
                                      ...prev,
                                      [service.id]: value,
                                    }))
                                  }
                                />
                                <textarea
                                  value={reviewByBooking[service.id] || ""}
                                  onChange={(event) =>
                                    setReviewByBooking((prev) => ({
                                      ...prev,
                                      [service.id]: event.target.value,
                                    }))
                                  }
                                  rows={3}
                                  placeholder="Share a short review (optional)"
                                  style={reviewInput}
                                />
                                <button
                                  onClick={() => handleRate(service)}
                                  disabled={
                                    !!ratingLoadingByBooking[service.id] ||
                                    !!ratedBookingIds[service.id]
                                  }
                                  style={{
                                    ...submitBtn,
                                    opacity:
                                      ratingLoadingByBooking[service.id] ||
                                      ratedBookingIds[service.id]
                                        ? 0.65
                                        : 1,
                                    cursor:
                                      ratingLoadingByBooking[service.id] ||
                                      ratedBookingIds[service.id]
                                        ? "not-allowed"
                                        : "pointer",
                                  }}
                                >
                                  {ratedBookingIds[service.id]
                                    ? "Rating Submitted"
                                    : ratingLoadingByBooking[service.id]
                                      ? "Submitting..."
                                      : "Submit Rating"}
                                </button>
                              </div>
                            )}
                                </>
                              );
                            })()}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {relatedMaterials.length > 0 && (
                    <div style={section}>
                      <h4 style={sectionHeading}>Material Orders</h4>
                      <div style={{ display: "grid", gap: "10px" }}>
                        {relatedMaterials.map((material) => (
                          <div key={material.id} style={miniCard}>
                            <div style={serviceTopRow} className="bookings-page__service-top-row">
                              <strong>{material.material_title}</strong>
                              <div style={getMaterialStageStyle(material)}>
                                {getMaterialStageLabel(material)}
                              </div>
                            </div>
                            <p style={muted}>
                              Quantity: {material.quantity} {material.unit || ""}
                            </p>
                            <p style={muted}>
                              Fulfilled By: {material.assignedShopLabel}
                            </p>
                            <p style={muted}>
                              Delivery:{" "}
                              {material.delivery_estimate || "Awaiting estimate"}
                            </p>
                            <p style={muted}>
                              Address: {material.resolvedAddressText || "N/A"}
                            </p>
                            <p style={muted}>
                              Delivery Status:{" "}
                              {formatStatus(material.delivery_status)}
                            </p>
                            <p style={muted}>
                              Priority Delivery:{" "}
                              {material.priority_delivery ? "Yes" : "No"}
                            </p>
                            <p style={serviceHint}>{getMaterialStageHint(material)}</p>
                            <p style={muted}>Amount: Rs {material.final_price}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const page = {
  background: "#000",
  minHeight: "100vh",
  color: "#fff",
  padding: "24px 16px 80px",
};

const container = {
  maxWidth: "1180px",
  margin: "0 auto",
};

const backBtn = {
  marginBottom: "18px",
  padding: "10px 14px",
  border: "1px solid #2e2e2e",
  background: "#111",
  color: "#fff",
  borderRadius: "10px",
  cursor: "pointer",
};

const heroCard = {
  background: "linear-gradient(135deg, #111, #1a1a1a)",
  border: "1px solid #242424",
  borderRadius: "22px",
  padding: "24px",
};

const heroText = {
  color: "#bdbdbd",
  marginTop: "10px",
  lineHeight: "1.7",
};

const emptyBox = {
  marginTop: "20px",
  background: "#111",
  border: "1px solid #262626",
  padding: "20px",
  borderRadius: "16px",
};

const errorBox = {
  marginTop: "20px",
  background: "#2a1414",
  border: "1px solid #6f2727",
  color: "#ffb4b4",
  padding: "20px",
  borderRadius: "16px",
};

const card = {
  background: "linear-gradient(180deg, #111, #0b0b0b)",
  border: "1px solid #252525",
  borderRadius: "20px",
  padding: "20px",
};

const topRow = {
  display: "flex",
  justifyContent: "space-between",
  gap: "14px",
  flexWrap: "wrap",
};

const statusWrap = {
  display: "flex",
  gap: "10px",
  flexWrap: "wrap",
};

const statusBadge = {
  padding: "8px 12px",
  borderRadius: "999px",
  background: "#173220",
  border: "1px solid #28593a",
  color: "#b8ffd0",
  fontSize: "13px",
};

const statusBadgeMuted = {
  padding: "8px 12px",
  borderRadius: "999px",
  background: "#171717",
  border: "1px solid #2b2b2b",
  color: "#d4d4d4",
  fontSize: "13px",
};

const infoGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: "14px",
  marginTop: "18px",
};

const infoBlock = {
  background: "#101010",
  border: "1px solid #242424",
  borderRadius: "16px",
  padding: "14px",
};

const price = {
  color: "#fff",
  fontWeight: "bold",
  fontSize: "22px",
  margin: "8px 0 0 0",
};

const muted = {
  color: "#c7c7c7",
  margin: "8px 0 0 0",
  lineHeight: "1.6",
};

const section = {
  marginTop: "18px",
};

const sectionHeading = {
  marginBottom: "10px",
};

const miniCard = {
  background: "#0f0f0f",
  border: "1px solid #232323",
  borderRadius: "14px",
  padding: "14px",
};

const serviceTopRow = {
  display: "flex",
  justifyContent: "space-between",
  gap: "12px",
  alignItems: "flex-start",
  flexWrap: "wrap",
};

const stageBadgeBase = {
  padding: "8px 12px",
  borderRadius: "999px",
  fontSize: "12px",
  fontWeight: "bold",
};

const stageBadgePending = {
  ...stageBadgeBase,
  background: "#2a230f",
  border: "1px solid #5a4a16",
  color: "#ffd76a",
};

const stageBadgeActive = {
  ...stageBadgeBase,
  background: "#173220",
  border: "1px solid #28593a",
  color: "#b8ffd0",
};

const stageBadgeDone = {
  ...stageBadgeBase,
  background: "#102a34",
  border: "1px solid #225569",
  color: "#a9e6ff",
};

const stageBadgeAlert = {
  ...stageBadgeBase,
  background: "#2a1414",
  border: "1px solid #6f2727",
  color: "#ffb4b4",
};

const serviceHint = {
  color: "#b5b5b5",
  margin: "8px 0 0 0",
  lineHeight: "1.6",
  fontSize: "13px",
};

const ratingCard = {
  marginTop: "14px",
  paddingTop: "14px",
  borderTop: "1px solid #232323",
  display: "grid",
  gap: "12px",
};

const ratingTitle = {
  margin: 0,
  color: "#f4f4f4",
  fontWeight: "600",
};

const reviewInput = {
  width: "100%",
  minHeight: "88px",
  borderRadius: "12px",
  border: "1px solid #2a2a2a",
  background: "#090909",
  color: "#fff",
  padding: "12px",
  resize: "vertical",
  boxSizing: "border-box",
};

const submitBtn = {
  width: "fit-content",
  padding: "10px 16px",
  borderRadius: "10px",
  border: "1px solid #735710",
  background: "linear-gradient(135deg, #facc15, #d4a80c)",
  color: "#111",
  fontWeight: "700",
};

export default MyBookings;
